import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import "./Navbar.css";
import { useAuth } from "../../views/AuthContext";
import { getBusiness } from "../../common/settingsStore";
import { DEFAULT_BUSINESS_LOGO } from "../../common/defaultLogo";

const NAV_LINKS = [
  { to: "/", name: "New Invoice", icon: "bi-plus-square" },
  { to: "/invoices", name: "Invoices", icon: "bi-receipt" },
  { to: "/customers", name: "Customers", icon: "bi-people" },
  { to: "/products", name: "Parts", icon: "bi-box-seam" },
  { to: "/settings", name: "Settings", icon: "bi-gear" },
];

const THEMES = [
  { value: "light", label: "Light", icon: "bi-sun-fill" },
  { value: "dark", label: "Dark", icon: "bi-moon-stars-fill" },
  { value: "auto", label: "Auto", icon: "bi-circle-half" },
];

const applyTheme = (theme) => {
  const dark =
    theme === "dark" ||
    (theme === "auto" && window.matchMedia("(prefers-color-scheme: dark)").matches);
  document.documentElement.setAttribute("data-bs-theme", dark ? "dark" : "light");
};

const Navbar = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const [business, setBusiness] = useState(getBusiness);

  useEffect(() => {
    const refresh = () => setBusiness(getBusiness());
    window.addEventListener("mm:business", refresh);
    return () => window.removeEventListener("mm:business", refresh);
  }, []);
  const [theme, setTheme] = useState(() => localStorage.getItem("theme") || "auto");

  useEffect(() => {
    localStorage.setItem("theme", theme);
    applyTheme(theme);
    if (theme !== "auto") return;
    // Follow the OS setting live while on "auto".
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme("auto");
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, [theme]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const current = THEMES.find((t) => t.value === theme) || THEMES[2];

  return (
    <nav className="navbar navbar-expand-lg enhanced-navbar sticky-top">
      <div className="container-xxl px-3 px-md-4">
        <Link className="navbar-brand" to="/">
          <img src={business.logo || DEFAULT_BUSINESS_LOGO} alt="" className="navbar-logo" />
          <span>{business.name}</span>
        </Link>
        <button
          className="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          <span className="navbar-toggler-icon" />
        </button>
        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav me-auto">
            {NAV_LINKS.map((item) => (
              <li key={item.to} className="nav-item">
                <NavLink end className="nav-link" to={item.to}>
                  <i className={`bi ${item.icon} me-2`} aria-hidden="true" />
                  {item.name}
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="d-flex align-items-center gap-2 flex-wrap navbar-actions">
            <div className="dropdown">
              <button
                className="btn theme-dropdown-btn dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                aria-label={`Theme: ${current.label}`}
              >
                <i className={`bi ${current.icon}`} />
                <span className="d-lg-none ms-2">Theme</span>
              </button>
              <ul className="dropdown-menu dropdown-menu-end theme-dropdown-menu">
                {THEMES.map((t) => (
                  <li key={t.value}>
                    <button
                      type="button"
                      className={`dropdown-item d-flex align-items-center ${theme === t.value ? "active" : ""}`}
                      onClick={() => setTheme(t.value)}
                    >
                      <i className={`bi ${t.icon} me-2`} />
                      {t.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            {isAuthenticated && (
              <>
                <span className="navbar-user d-none d-xl-inline" title={user}>
                  <i className="bi bi-person-circle me-1" />
                  {user}
                </span>
                <button onClick={handleLogout} type="button" className="btn logout-btn">
                  <i className="bi bi-box-arrow-right me-1" />
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
